import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import ReactTooltip from 'react-tooltip';

import { getAsset } from '../../../../../../../../../util/assets';
import Badge from '../../../../../../../../../components/global/Badge';
import PriceDisplay from '../../../../../../../../../components/global/PriceDisplay';
import Button from '../../../../../../../../../components/global/Button';
import AddedToCartLabel from '../../../PhotoPurchasingOptions/AddedToCartLabel';
import { getIdentityCallingName } from '../../helpers';
import LimitedTimeOfferCta from './LimitedTimeOfferCta';
import useOpenModal from '../../../../../../../../../hooks/useOpenModal';
import { SUBSCRIPTION_IDENTITY_SELECT_MODAL } from '../../../../../All/components/AllPhotos/components/SubscriptionIdentitySelectModal';

const propTypes = {
  className: PropTypes.string,
  readOnly: PropTypes.bool,
  albumFolder: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }).isRequired,
  identity: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  cartItem: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  onSubscriptionToggle: PropTypes.func.isRequired,
  allowIdentitySelection: PropTypes.bool,
  disabled: PropTypes.bool,
};

const AlbumFolderSubscriptionOption = ({
  className,
  readOnly,
  albumFolder,
  identity,
  cartItem,
  onSubscriptionToggle,
  allowIdentitySelection,
  disabled = false,
}) => {
  const openModal = useOpenModal();

  const classes = classNames({
    [className]: !!className,
    'purchasing-option-item': true,
    'purchasing-option-item-album-folder': true,
    'is-selected': !!cartItem,
  });

  const name = identity ? getIdentityCallingName(identity, false) : '';

  const renderSubscriptionPrice = () => {
    const price = albumFolder.price.value;
    const discountPrice = albumFolder.limited_time_offer_active
      ? albumFolder.limited_time_offer_price.value
      : undefined;

    return (
      <PriceDisplay
        price={discountPrice || price}
        currency={albumFolder.price.currency}
        oldPrice={price}
        showDiscount
        trimDecimalZeros={false}
      />
    );
  };

  return (
    <div className={classes}>
      <div>
        <div className="purchasing-option-item-info-extra">
          <Badge
            badgeStyle="yellow"
            className="purchasing-option-item-info-badge"
          >
            {renderSubscriptionPrice()}
          </Badge>
        </div>

        <div className="purchasing-option-item-thumb">
          <img width={75} src={getAsset('/photopass.svg')} alt="" />
        </div>
      </div>

      <div className="purchasing-option-item-content-wrap">
        <div className="purchasing-option-item-title">Digital Photo Pass</div>

        <div className="purchasing-option-item-content mb-2">
          {readOnly ? (
            <p className="text-sm lh-normal text-dark text-bold">
              Each pass includes hi&ndash;res digital access to all the matching
              photos for a single identity.
            </p>
          ) : (
            <p className="text-sm lh-normal text-dark text-bold">
              Unlock access to hi&ndash;res downloads for EVERY photo of{' '}
              <strong>{name}</strong> in this gallery.
            </p>
          )}

          {albumFolder.limited_time_offer_active && (
            <LimitedTimeOfferCta
              className="d-block text-sm text-italic mt-1"
              albumFolder={albumFolder}
            />
          )}
        </div>

        {!readOnly && (
          <>
            <div
              data-tip={
                disabled
                  ? 'Digital Photo Passes are only available for matched photos. Joining the event is required.'
                  : ''
              }
              data-for="disabledOptionTooltip"
            >
              <Button
                btnStyle={classNames({
                  primary: !cartItem,
                  info: !!cartItem,
                })}
                className={classNames({
                  'purchasing-options-item-btn': true,
                  'btn-purchase-options-in-cart':
                    !!cartItem && !allowIdentitySelection,
                })}
                disabled={disabled}
                onClick={() => {
                  if (!disabled) {
                    if (allowIdentitySelection) {
                      openModal(SUBSCRIPTION_IDENTITY_SELECT_MODAL, {
                        albumFolderId: albumFolder.id,
                        onIdentitySelect: selectedIdentity =>
                          onSubscriptionToggle({ selectedIdentity }),
                      });
                    } else {
                      onSubscriptionToggle();
                    }
                  }
                }}
                data-testid="btn-album-folder-subscription"
              >
                {cartItem && !allowIdentitySelection ? (
                  <AddedToCartLabel />
                ) : (
                  <Fragment>Add to cart</Fragment>
                )}
              </Button>
            </div>

            <ReactTooltip
              id="disabledOptionTooltip"
              place="top"
              effect="solid"
              multiline
            />
          </>
        )}
      </div>
    </div>
  );
};

AlbumFolderSubscriptionOption.propTypes = propTypes;

export default AlbumFolderSubscriptionOption;
